import { ToggleOff, ToggleOn } from '@mui/icons-material';
import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from '@mui/material';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { TrashIcon } from '../../../../../assets/icons';
import {
  ConfirmDialog,
  ConfirmDialogOptions,
  EmptyTable,
  FilterTableMenu,
  StatusItem,
} from '../../../../../components';
import { useLoading, useNotify } from '../../../../../hooks';
import { CampusService, WorkPlaceService } from '../../../../../services';
import { TablePaginatorOptions } from '../../../../../utils/consts';
import { DirectionOptions } from '../../../../../utils/enums';
import { Campus, ClaveCentroTrabajo } from '../../../../../utils/models';
import { WorkPlaceTableColumn } from '../../enums/WorkPlaceTableColumn';
import { useWorkPlace } from '../../hook/useWorkPlace';
import { WorkPlaceTableActions } from '../WorkPlaceTableActions/WorkPlaceTableActions';

interface Props {
  onReloadData: () => void;
  onTableFilter: (
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    asc: string,
  ) => void;
}

export const WorkPlaceTable: FC<Props> = ({ onReloadData, onTableFilter }) => {
  const campusService = new CampusService();
  const workPlaceService = new WorkPlaceService();

  const theme = useTheme();
  const palette = theme.palette;
  const { notify, setNotify } = useNotify();
  const { setLoading } = useLoading();
  const [campusList, setCampusList] = useState<Campus[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogOptions>({
    open: false,
    title: '',
    subTitle: '',
    confirmText: '',
    cancelText: '',
    onConfirm: () => null,
  });

  const {
    workCenterKeyList,
    paginatorOptions,
    setPage,
    setRowsPerPage,
    rowsPerPage,
    page,
    modalRecord,
    setModal,
    setWorkCenterKeyList,
    orderBy,
    dir,
    setOrder,
  } = useWorkPlace();

  const handleChangePage = (event: unknown, newPage: number) => {
    const value = newPage + 1;
    setPage(value);
    onTableFilter(rowsPerPage, value, orderBy, dir);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    const valuePage = 1;
    setPage(valuePage);
    onTableFilter(value, valuePage, orderBy, dir);
  };

  const getAllCampus = async () => {
    setLoading(true);
    await campusService
      .getAllCampus()
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          const data: Campus[] = res.data.data.map((user) =>
            new Campus().deserialize(user),
          );
          setCampusList(data);
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
    setLoading(false);
  };

  const updateStatusWorkCenterKey = async (
    campusId: string,
    program: string,
    status: boolean,
  ) => {
    setLoading(true);
    await workPlaceService
      .updateStatusWorkCenterKey(campusId, program, status)
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          setNotify({
            ...notify,
            open: true,
            message: 'Se actualizó el CCT correctamente.',
            type: 'success',
          });
          onReloadData();
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setNotify({
          ...notify,
          open: true,
          message: 'Error inesperado',
          type: 'error',
        });
        setLoading(false);
      });
  };

  const deleteWorkCenterKey = async (campusId: string, program: string) => {
    setLoading(true);
    await workPlaceService
      .deleteWorkCenterKey(campusId, program)
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          setNotify({
            ...notify,
            open: true,
            message: 'Se eliminó el CCT correctamente.',
            type: 'success',
          });
          onReloadData();
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const setCampusName = (campusId: string) => {
    const aux = campusList.find((campus) => campus.id === campusId);
    return aux && aux.nombre ? aux.nombre : '';
  };

  const onAlert = async (
    workPlaceKey: ClaveCentroTrabajo,
    type: 'edit' | 'delete',
    subTitle: string,
  ) => {
    setConfirmDialog({
      title:
        type === 'edit' ? 'Modificar el status del CCT' : 'Eliminar el CCT',
      subTitle,
      confirmText:
        type === 'edit'
          ? workPlaceKey.activo
            ? 'Sí, desactivar'
            : 'Sí, activar'
          : 'Sí, eliminar',
      cancelText: 'No, Cancelar',
      open: true,
      startIcon:
        type === 'edit' ? (
          workPlaceKey.activo ? (
            <ToggleOn sx={{ color: 'grayCustom.main' }} />
          ) : (
            <ToggleOff sx={{ color: 'grayCustom.main' }} />
          )
        ) : (
          <TrashIcon fill={palette.grayCustom.main} />
        ),
      endIcon:
        type === 'edit' ? (
          <StatusItem
            status={!workPlaceKey.activo}
            bgColor={workPlaceKey.activo ? palette.grayCustom.darker : null}
            bgText={workPlaceKey.activo ? palette.grayCustom.main : null}
          />
        ) : null,
      onConfirm: () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        switch (type) {
          case 'delete':
            deleteWorkCenterKey(workPlaceKey.campus, workPlaceKey.programa);
            break;
          case 'edit':
            updateStatusWorkCenterKey(
              workPlaceKey.campus,
              workPlaceKey.programa,
              !workPlaceKey.activo,
            );
            break;
        }
      },
    });
  };

  const onOrderUpward = (columnName: WorkPlaceTableColumn) => {
    setOrder(columnName, DirectionOptions.ASC);
    onTableFilter(rowsPerPage, page, columnName, DirectionOptions.ASC);
  };

  const onOrderFalling = (columnName: WorkPlaceTableColumn) => {
    setOrder(columnName, DirectionOptions.DESC);
    onTableFilter(rowsPerPage, page, columnName, DirectionOptions.DESC);
  };

  useEffect(() => {
    getAllCampus();
  }, []);

  return (
    <>
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell variant="head">
                Campus{' '}
                <FilterTableMenu
                  onOrderUpward={() =>
                    onOrderUpward(WorkPlaceTableColumn.CAMPUS)
                  }
                  onOrderFalling={() =>
                    onOrderFalling(WorkPlaceTableColumn.CAMPUS)
                  }
                />
              </TableCell>
              <TableCell variant="head">
                Programa{' '}
                <FilterTableMenu
                  onOrderUpward={() =>
                    onOrderUpward(WorkPlaceTableColumn.PROGRAMA)
                  }
                  onOrderFalling={() =>
                    onOrderFalling(WorkPlaceTableColumn.PROGRAMA)
                  }
                />
              </TableCell>
              <TableCell variant="head">
                CCT{' '}
                <FilterTableMenu
                  onOrderUpward={() => onOrderUpward(WorkPlaceTableColumn.CCT)}
                  onOrderFalling={() =>
                    onOrderFalling(WorkPlaceTableColumn.CCT)
                  }
                />
              </TableCell>
              <TableCell variant="head">
                Status{' '}
                <FilterTableMenu
                  onOrderUpward={() =>
                    onOrderUpward(WorkPlaceTableColumn.ESTATUS)
                  }
                  onOrderFalling={() =>
                    onOrderFalling(WorkPlaceTableColumn.ESTATUS)
                  }
                />
              </TableCell>
              <TableCell variant="head" align="center" sx={{ pt: 1 }}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workCenterKeyList.map((row) => (
              <TableRow
                key={row.campus + row.programa}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{setCampusName(row.campus)}</TableCell>
                <TableCell>{row.programa}</TableCell>
                <TableCell>{row.cct}</TableCell>
                <TableCell>
                  {' '}
                  <StatusItem status={row.activo} />{' '}
                </TableCell>
                <TableCell align="center">
                  <WorkPlaceTableActions
                    workPlace={row}
                    onEdit={() =>
                      setModal({
                        ...modalRecord,
                        show: true,
                        type: 'edit',
                        campusId: row.campus,
                        programa: row.programa,
                      })
                    }
                    onChangeStatus={() =>
                      onAlert(
                        row,
                        'edit',
                        `¿Deseas cambiar el status del CCT a “${
                          row.activo ? 'INACTIVO' : 'ACTIVO'
                        }”?`,
                      )
                    }
                    onDelete={() =>
                      onAlert(
                        row,
                        'delete',
                        `¿Deseas eliminar el CCT “${row.cct}”?`,
                      )
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <EmptyTable elements={workCenterKeyList} />
      <Divider />
      <TablePagination
        rowsPerPageOptions={TablePaginatorOptions.rowsPerPageOptions}
        component="div"
        count={paginatorOptions.totalCount}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        SelectProps={{
          inputProps: {
            'aria-label': 'Filas por página',
          },
          native: true,
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};
